import {
  Box,
  Button,
  ButtonProps,
  Card,
  Checkbox,
  CheckboxProps,
  Flex,
  FlexProps,
  Grid,
  GridProps,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectProps,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import {
  type DeploymentStatus,
  customerDataApiConfigSchema,
} from '@helo/shared'
import React, { ForwardedRef, useEffect } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { MinusIcon } from '../../../components/Icons/MinusIcon'
import { PlusIcon } from '../../../components/Icons/PlusIcon'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { SelectInput } from '../../../components/inputs/SelectInput'
import { TextInput } from '../../../components/inputs/TextInput'
import {
  formatAdminDate,
  formatAdminDateTime,
  formatAdminTime,
} from '../../../utils/dates'

export type UpdateCustomerDataApiConfigInput = z.infer<
  typeof customerDataApiConfigSchema
>

export type ConfigListItem = {
  id: string
  createdDate?: string
  updatedDate?: string
  isActive?: boolean
  lastUpdatedBy?: { name?: string | null; email?: string | null } | null
  lastUpdatedByUser?: string | null
  latestDeployment?: {
    id: string
    status: DeploymentStatus
    attempt?: number
    startedAt?: string
  } | null
  subgraphConfigs?: unknown
}

export type VersionRailMarker =
  | { kind: 'live' }
  | { kind: 'running'; deploymentId: string }
  | { kind: 'failed'; deploymentId: string }
  | { kind: 'never-deployed' }
  | null

/**
 * The version rail only surfaces the minimum signal needed to triage a row:
 * - LIVE: green badge on the currently-live config
 * - running: a deployment is in flight (linked, muted)
 * - failed: last deployment failed (linked, red text)
 * - never-deployed: no deployment exists (red text)
 * All other states (canceled, superseded) are intentionally hidden to keep
 * the rail dense and quiet.
 */
const deriveVersionRailMarker = (config: ConfigListItem): VersionRailMarker => {
  if (config.isActive) return { kind: 'live' }
  const latest = config.latestDeployment
  if (!latest) return { kind: 'never-deployed' }
  if (latest.status === 'PENDING' || latest.status === 'IN_PROGRESS') {
    return { kind: 'running', deploymentId: latest.id }
  }
  if (latest.status === 'FAILED') {
    return { kind: 'failed', deploymentId: latest.id }
  }
  return null
}

export const transformConfigForForm = (data: any) => ({
  ...data,
  subgraphConfigs:
    data?.subgraphConfigs?.map((subgraph: any) => ({
      ...subgraph,
      customerDataSources:
        subgraph.customerDataSources?.map((dataSource: any) => ({
          ...dataSource,
          currentVersion:
            dataSource.currentVersion ||
            (dataSource.customerDatabases?.length > 0
              ? dataSource.customerDatabases[0].version
              : ''),
        })) || [],
    })) || [],
})

export const configsListVariables = { first: 200, skip: 0 }

// -- Top "currently live" header strip ------------------------------------- //

export const LiveConfigStrip = ({
  activeConfig,
  onOpenInEditor,
}: {
  activeConfig: ConfigListItem | null | undefined
  onOpenInEditor: () => void
}) => {
  if (!activeConfig) {
    return (
      <Card p="12px">
        <Text fontSize="sm" color="presets.neutral.70">
          No config is currently live. Save a draft below to start the first
          pipeline run.
        </Text>
      </Card>
    )
  }
  const latest = activeConfig.latestDeployment
  return (
    <Card p="12px">
      <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap="2">
        <Flex direction="column" gap="1" minW="0">
          <Flex align="center" gap="2">
            <AdminBadge colorScheme="green">LIVE</AdminBadge>
            <Text fontWeight="600" fontSize="sm">
              Version{' '}
              {activeConfig.createdDate
                ? formatAdminDateTime(activeConfig.createdDate)
                : activeConfig.id.slice(0, 8)}
            </Text>
          </Flex>
          <Text fontSize="11px" color="presets.neutral.70">
            Last updated by{' '}
            {activeConfig.lastUpdatedBy?.name ??
              activeConfig.lastUpdatedBy?.email ??
              activeConfig.lastUpdatedByUser ??
              'unknown'}
            {latest?.startedAt
              ? ` · Deployed ${formatAdminDateTime(latest.startedAt) ?? ''}`
              : ''}
            {latest?.attempt && latest.attempt > 1
              ? ` (attempt ${latest.attempt})`
              : ''}
          </Text>
        </Flex>
        <Flex gap="2">
          <Button
            as={Link}
            to={`/admin/deployments/${latest?.id ?? ''}`}
            size="xs"
            variant="outline"
            isDisabled={!latest?.id}
          >
            View pipeline run
          </Button>
          <Button size="xs" onClick={onOpenInEditor}>
            Open active in editor
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}

// -- Left version rail ----------------------------------------------------- //

export const VersionRail = ({
  configs,
  activeConfigId,
  viewedConfigId,
  onSelect,
}: {
  configs: ConfigListItem[]
  activeConfigId: string | null
  viewedConfigId: string | null
  onSelect: (config: ConfigListItem) => void
}) => {
  return (
    <Flex
      direction="column"
      width="210px"
      minW="210px"
      bg="presets.neutral.5"
      borderRadius="md"
      borderWidth="1px"
      borderColor="presets.neutral.20"
      maxHeight="72vh"
      overflowY="auto"
      data-testid="config-version-rail"
    >
      <Text
        fontSize="xs"
        fontWeight="700"
        textTransform="uppercase"
        color="presets.neutral.70"
        px="10px"
        py="6px"
        borderBottom="1px solid"
        borderColor="presets.neutral.20"
      >
        Config versions
      </Text>
      {configs.length === 0 && (
        <Text fontSize="xs" color="presets.neutral.70" p="12px">
          No saved versions yet.
        </Text>
      )}
      {configs.map(c => {
        const marker = deriveVersionRailMarker({
          ...c,
          isActive: c.id === activeConfigId,
        })
        const isSelected = viewedConfigId === c.id
        const dateLabel = c.createdDate
          ? formatAdminDate(c.createdDate)
          : c.id.slice(0, 8)
        const timeLabel = c.createdDate ? formatAdminTime(c.createdDate) : null
        const author =
          c.lastUpdatedBy?.name ??
          c.lastUpdatedBy?.email ??
          c.lastUpdatedByUser ??
          null
        return (
          <Flex
            key={c.id}
            align="flex-start"
            gap="2"
            px="10px"
            py="6px"
            borderBottom="1px solid"
            borderColor="presets.neutral.10"
            bg={isSelected ? 'presets.turquoise.10' : 'transparent'}
            _hover={{ bg: 'presets.neutral.10', cursor: 'pointer' }}
            onClick={() => onSelect(c)}
            data-testid={`config-version-${c.id}`}
          >
            <Flex direction="column" gap="0" minW="0" flex="1">
              <Text
                fontSize="12px"
                lineHeight="1.2"
                fontWeight={isSelected ? '600' : '500'}
              >
                {dateLabel}
              </Text>
              {timeLabel && (
                <Text
                  fontSize="11px"
                  lineHeight="1.2"
                  color="presets.neutral.70"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {timeLabel}
                </Text>
              )}
              {author && (
                <Text
                  fontSize="10px"
                  lineHeight="1.2"
                  color="presets.neutral.70"
                  mt="1"
                  noOfLines={1}
                >
                  {author}
                </Text>
              )}
            </Flex>
            <VersionRailMarkerView marker={marker} />
          </Flex>
        )
      })}
    </Flex>
  )
}

export const VersionRailMarkerView = ({
  marker,
}: {
  marker: VersionRailMarker
}) => {
  if (!marker) return null
  if (marker.kind === 'live') {
    return (
      <AdminBadge colorScheme="green" mt="1">
        LIVE
      </AdminBadge>
    )
  }
  if (marker.kind === 'never-deployed') {
    return (
      <Text
        fontSize="10px"
        color="presets.pink.70"
        fontWeight="600"
        mt="1"
        whiteSpace="nowrap"
      >
        never deployed
      </Text>
    )
  }
  if (marker.kind === 'failed') {
    return (
      <Link
        to={`/admin/deployments/${marker.deploymentId}`}
        onClick={e => e.stopPropagation()}
        style={{
          fontSize: '10px',
          fontWeight: 600,
          color: 'var(--chakra-colors-presets-pink-70)',
          marginTop: '2px',
          whiteSpace: 'nowrap',
        }}
      >
        failed
      </Link>
    )
  }
  return (
    <Link
      to={`/admin/deployments/${marker.deploymentId}`}
      onClick={e => e.stopPropagation()}
      style={{
        fontSize: '10px',
        color: 'var(--chakra-colors-presets-neutral-70)',
        marginTop: '2px',
        whiteSpace: 'nowrap',
      }}
    >
      running
    </Link>
  )
}

// -- Editor header / action bar ------------------------------------------- //

export const EditorHeader = ({
  viewedConfig,
  isViewingActive,
  isDirty,
  isSubmitting,
  isRerunning,
  onRerunThisVersion,
}: {
  viewedConfig: ConfigListItem | null
  isViewingActive: boolean
  isDirty: boolean
  isSubmitting: boolean
  isRerunning: boolean
  onRerunThisVersion: () => void
}) => {
  return (
    <Flex justify="space-between" align="center" flexWrap="wrap" gap="3">
      <Flex direction="column" gap="0.5" minW="0">
        <Flex align="center" gap="2">
          <Text as="h2" fontSize="16px" fontWeight="600" lineHeight="1.2">
            {isDirty
              ? 'New draft'
              : viewedConfig
                ? `Editing version ${
                    viewedConfig.createdDate
                      ? formatAdminDateTime(viewedConfig.createdDate)
                      : viewedConfig.id.slice(0, 8)
                  }`
                : 'New config'}
          </Text>
          {!isDirty && viewedConfig && (
            <AdminBadge colorScheme={isViewingActive ? 'green' : 'gray'}>
              {isViewingActive ? 'LIVE' : 'HISTORICAL'}
            </AdminBadge>
          )}
        </Flex>
        {!isDirty && viewedConfig?.latestDeployment && (
          <Text fontSize="11px" color="presets.neutral.70">
            Latest run:{' '}
            <Link
              to={`/admin/deployments/${viewedConfig.latestDeployment.id}`}
              style={{
                color: 'var(--chakra-colors-presets-turquoise-70)',
              }}
            >
              {viewedConfig.latestDeployment.status}
              {viewedConfig.latestDeployment.attempt
                ? ` · attempt ${viewedConfig.latestDeployment.attempt}`
                : ''}
            </Link>
          </Text>
        )}
      </Flex>
      <Flex gap="2" alignItems="center">
        {!isDirty && viewedConfig?.latestDeployment?.id && (
          <Tooltip label="Start a new pipeline run using this exact config version (latest-wins: in-flight runs are cancelled).">
            <Button
              size="xs"
              variant="outline"
              onClick={onRerunThisVersion}
              isLoading={isRerunning}
              leftIcon={isRerunning ? <Spinner size="sm" /> : undefined}
              data-testid="rerun-this-version-button"
            >
              Rerun this version
            </Button>
          </Tooltip>
        )}
        <Tooltip
          label={
            isDirty
              ? 'Creates a new config version and starts a pipeline run'
              : 'Edit the form to enable Save & Deploy'
          }
        >
          <Button
            size="xs"
            type="submit"
            isDisabled={!isDirty || isSubmitting}
            leftIcon={isSubmitting ? <Spinner size="sm" /> : undefined}
            data-testid="publish-config-button"
          >
            {isSubmitting ? 'Saving & deploying...' : 'Save & Deploy'}
          </Button>
        </Tooltip>
      </Flex>
    </Flex>
  )
}

// -- Remaining sub-forms (unchanged behaviour, copied from prior version) - //

export const DataSourceFields = ({
  subgraphIndex,
  organizations,
}: {
  subgraphIndex: number
  organizations:
    | {
        id: string
        name: string
        displayName: string
      }[]
    | undefined
}) => {
  const { control, watch, setValue, trigger, resetField, formState } =
    useFormContext<UpdateCustomerDataApiConfigInput>()
  const {
    fields,
    append: appendDataSource,
    remove: removeDataSource,
  } = useFieldArray<UpdateCustomerDataApiConfigInput>({
    name: `subgraphConfigs.${subgraphIndex}.customerDataSources`,
    control,
  })

  const customerDataSources = watch(
    `subgraphConfigs.${subgraphIndex}.customerDataSources`,
  )

  const handleAddDatasource = () => {
    appendDataSource({
      id: '',
      organizationName: '',
      currentVersion: '',
      isMainIntrospectionDB: false,
      customerDatabases: [],
    })
  }

  useEffect(() => {
    if (customerDataSources) {
      customerDataSources.forEach((dataSource, dataSourceIndex) => {
        const currentVersion = dataSource.currentVersion
        const availableVersions =
          dataSource.customerDatabases?.map(db => db.version) || []
        if (currentVersion && !availableVersions.includes(currentVersion)) {
          const fieldPath = `subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.currentVersion`
          ;(resetField as any)(fieldPath, { defaultValue: '' })
          ;(trigger as any)(fieldPath)
        }
      })
    }
  }, [customerDataSources, resetField, trigger, formState, subgraphIndex])

  return (
    <>
      <Flex alignItems="center" gap="3">
        <Text fontSize="14px" py="4px" fontWeight="600">
          Customer Data Sources
        </Text>
        {fields.length > 0 && (
          <AddButton
            label="Add"
            leftIcon={undefined}
            variant="outline"
            onClick={handleAddDatasource}
          />
        )}
      </Flex>
      {fields.length > 0 && (
        <Grid
          templateColumns="repeat(auto-fill, minmax(620px, 1fr))"
          gap="10px"
        >
          {fields.map((dataSource, dataSourceIndex, dataSourceArray) => (
            <FieldGridCard key={dataSource.id} bg="presets.neutral.10">
              <DeletePopover
                triggerLabel="Delete Data Source"
                onDelete={() => removeDataSource(dataSourceIndex)}
              />
              <FieldGrid mb="10px">
                <InputWithLabel
                  label="Data Source ID"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.id`}
                />
                <SelectWithLabel
                  label="Organization"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.organizationName`}
                  options={
                    organizations?.map(organization => ({
                      label: organization.displayName,
                      value: organization.name,
                    })) ?? []
                  }
                />
                <SelectWithLabel
                  label="Current Version"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.currentVersion`}
                  options={
                    customerDataSources?.[
                      dataSourceIndex
                    ]?.customerDatabases?.map(database => ({
                      label: database.version,
                      value: database.version,
                    })) || []
                  }
                />
                <CheckboxWithLabel
                  label="Main Introspection DB"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.isMainIntrospectionDB`}
                  onChange={() => {
                    dataSourceArray.forEach((_item, itemIndex) => {
                      if (itemIndex !== dataSourceIndex) {
                        setValue(
                          `subgraphConfigs.${subgraphIndex}.customerDataSources.${itemIndex}.isMainIntrospectionDB`,
                          false,
                        )
                      }
                    })
                  }}
                />
              </FieldGrid>
              <DatabaseFields
                subgraphIndex={subgraphIndex}
                dataSourceIndex={dataSourceIndex}
              />
            </FieldGridCard>
          ))}
        </Grid>
      )}
      <AddButton label="Add Datasource" onClick={handleAddDatasource} />
    </>
  )
}

export const DatabaseFields = ({
  subgraphIndex,
  dataSourceIndex,
}: {
  subgraphIndex: number
  dataSourceIndex: number
}) => {
  const { control } = useFormContext<UpdateCustomerDataApiConfigInput>()
  const {
    fields,
    append: appendDatabase,
    remove: removeDatabase,
  } = useFieldArray<UpdateCustomerDataApiConfigInput>({
    name: `subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases`,
    control,
  })

  const handleAddDatabase = () => {
    ;(appendDatabase as any)({
      id: '',
      version: '',
      host: '',
      port: 5432,
      databaseName: '',
      cubeApiUrl: '',
    })
  }

  return (
    <>
      <Flex alignItems="center" gap="3">
        <Text fontSize="14px" py="4px" fontWeight="600">
          Customer Databases
        </Text>
        {fields.length > 0 && (
          <AddButton
            label="Add"
            leftIcon={undefined}
            variant="outline"
            onClick={handleAddDatabase}
          />
        )}
      </Flex>
      {fields.length > 0 && (
        <Grid templateColumns="repeat(auto-fit, minmax(520px, 1fr))" gap="8px">
          {fields.map((database, databaseIndex) => (
            <FieldGridCard
              bg="presets.neutral.20"
              key={database.id + databaseIndex}
            >
              <DeletePopover
                triggerLabel="Delete Database"
                onDelete={() => removeDatabase(databaseIndex)}
              />
              <FieldGrid>
                <InputWithLabel
                  label="ID"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases.${databaseIndex}.id`}
                />
                <InputWithLabel
                  label="Version"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases.${databaseIndex}.version`}
                />
                <InputWithLabel
                  label="Host"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases.${databaseIndex}.host`}
                  data-testid={`database-host-${subgraphIndex}-${dataSourceIndex}-${databaseIndex}`}
                />
                <InputWithLabel
                  label="Port"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases.${databaseIndex}.port`}
                  type="number"
                />
                <InputWithLabel
                  label="Database Name"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases.${databaseIndex}.databaseName`}
                />
                <InputWithLabel
                  label="Cube API URL"
                  name={`subgraphConfigs.${subgraphIndex}.customerDataSources.${dataSourceIndex}.customerDatabases.${databaseIndex}.cubeApiUrl`}
                  placeholder="https://cube.example.com/cubejs-api/graphql"
                />
              </FieldGrid>
            </FieldGridCard>
          ))}
        </Grid>
      )}
      <AddButton label="Add Database" onClick={handleAddDatabase} />
    </>
  )
}

export const InputWithLabel = ({
  label,
  name,
  type = 'text',
  ...props
}: {
  label: string
  name: string
  type?: string
} & React.InputHTMLAttributes<HTMLInputElement>) => {
  const { register, formState, control } = useFormContext()
  const errorMessage = getErrorMessage(formState.errors, name)
  const isInvalid = !!errorMessage
  const { size: _size, ...inputProps } = props

  return (
    <>
      <Text fontSize="11px" color="presets.neutral.70">
        {label}
      </Text>
      <Flex direction="column" gap="2px">
        {type === 'number' ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <TextInput
                isInvalid={isInvalid}
                placeholder={label}
                {...inputProps}
                {...field}
                value={field.value?.toString() || ''}
                onChange={e => {
                  const value = e.target.value
                  if (value === '') field.onChange('')
                  else {
                    const numValue = Number(value)
                    field.onChange(isNaN(numValue) ? value : numValue)
                  }
                }}
              />
            )}
          />
        ) : (
          <TextInput
            isInvalid={isInvalid}
            placeholder={label}
            {...inputProps}
            {...register(name)}
          />
        )}
        {errorMessage && (
          <Text color="presets.pink.70" fontSize="10px" textAlign="left">
            {errorMessage}
          </Text>
        )}
      </Flex>
    </>
  )
}

export const CheckboxWithLabel = ({
  label,
  name,
  onChange,
  ...props
}: { label: string; name: string } & CheckboxProps) => {
  const { formState, control } = useFormContext()
  const errorMessage = getErrorMessage(formState.errors, name)
  const isInvalid = !!errorMessage

  return (
    <>
      <Text fontSize="11px" color="presets.neutral.70">
        {label}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Checkbox
            size="sm"
            isInvalid={isInvalid}
            isChecked={field.value}
            onChange={e => {
              field.onChange(e.target.checked)
              onChange?.(e)
            }}
            {...props}
          />
        )}
      />
      {errorMessage && (
        <Text color="presets.pink.70" fontSize="10px" textAlign="left">
          {errorMessage}
        </Text>
      )}
    </>
  )
}

export const SelectWithLabel = ({
  label,
  name,
  options,
  ...props
}: {
  label: string
  name: string
  options: { label: string; value: string }[]
} & SelectProps) => {
  const { control, formState } = useFormContext()
  const errorMessage = getErrorMessage(formState.errors, name)

  return (
    <>
      <Text fontSize="11px" color="presets.neutral.70">
        {label}
      </Text>
      <Flex direction="column" gap="2px">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <SelectInput
              value={{
                label:
                  options?.find(option => option.value === field.value)
                    ?.label || '',
                value: field.value || '',
              }}
              options={options}
              onChange={value => field.onChange(value?.value || '')}
            />
          )}
        />
        {errorMessage && (
          <Text color="presets.pink.70" fontSize="10px" textAlign="left">
            {errorMessage}
          </Text>
        )}
      </Flex>
    </>
  )
}

export const AddButton = ({
  label,
  ...props
}: { label: string } & ButtonProps) => (
  <Button
    leftIcon={<PlusIcon />}
    size="xs"
    variant="tertiary"
    justifyContent="flex-start"
    w="fit-content"
    {...props}
  >
    {label}
  </Button>
)

export const DeleteButton = React.forwardRef(
  (
    { label, ...props }: { label: string } & ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => (
    <Button
      leftIcon={<MinusIcon />}
      size="xs"
      variant="tertiary"
      color="presets.pink.70"
      position="absolute"
      right="0"
      ref={ref}
      {...props}
    >
      {label}
    </Button>
  ),
)

export const FieldGridCard = ({
  children,
  ...props
}: { children: React.ReactNode } & FlexProps) => (
  <Card
    direction="column"
    gap="10px"
    p="12px"
    position="relative"
    overflow="visible"
    {...props}
  >
    {children}
  </Card>
)

export const FieldGrid = ({
  children,
  ...props
}: { children: React.ReactNode } & GridProps) => (
  <Grid
    templateColumns="112px minmax(160px, 360px)"
    columnGap="6"
    rowGap="2"
    alignItems="center"
    {...props}
  >
    {children}
  </Grid>
)

export const DeletePopover = ({
  triggerLabel,
  onDelete,
}: {
  triggerLabel: string
  onDelete: () => void
}) => (
  <Popover isLazy closeOnBlur>
    {({ onClose }) => (
      <Flex position="absolute" justifyContent="flex-end" right="3" top="3">
        <PopoverTrigger>
          <DeleteButton label={triggerLabel} />
        </PopoverTrigger>
        <PopoverContent maxW="300px" w="auto">
          <PopoverArrow />
          <PopoverBody>
            <Flex direction="column" gap="16px">
              <Flex justifyContent="flex-end" gap="16px">
                <Button variant="dangerButton" onClick={onDelete}>
                  {triggerLabel}
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </Flex>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Flex>
    )}
  </Popover>
)

function getErrorMessage(obj: any, path: string) {
  return path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj)
    ?.message as string | undefined
}
